import { useState, useRef, useEffect } from "react";
import { API } from "@/lib/api";
import { COMPANY } from "@/lib/site";
import { Send, X, Plus, Mic, AudioLines, ChevronDown, Square, Loader2, Volume2 } from "lucide-react";
import { useVoice } from "@/hooks/useVoice";
import { toast } from "sonner";

const MODELS = ["Luchii-70B", "Luchii-7B", "Luchii-1B", "Luchii-200M"];
const TIER = { "Luchii-70B": "Deep", "Luchii-7B": "Deep", "Luchii-1B": "Fast", "Luchii-200M": "Fast" };

export default function ChatWidget({ open, setOpen }) {
  const [sessionId] = useState(() => `visitor_${Math.random().toString(36).slice(2, 12)}`);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "I am Luchii — Ask me anything." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [model, setModel] = useState("Luchii-70B");
  const scrollRef = useRef(null);
  const voice = useVoice();
  const [thinking, setThinking] = useState(false);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return "";
    let full = "";
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg, model }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          let chunk;
          try { chunk = JSON.parse(data); } catch { chunk = data; }
          full += chunk;
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Luchii is momentarily unavailable. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
    return full;
  };

  const onMic = async () => {
    if (voice.recording === "dictate") {
      const text = await voice.stop();
      if (text) setInput((v) => (v ? `${v} ${text}` : text));
    } else if (!voice.recording) {
      voice.stopSpeaking();
      await voice.start("dictate");
    }
  };

  const finishTalk = async () => {
    setThinking(true);
    try {
      const text = await voice.stop();
      if (!text) return;
      const reply = await send(text);
      if (reply) await voice.speak(reply);
    } finally {
      setThinking(false);
    }
  };

  const onTalk = async () => {
    if (voice.speaking) return voice.stopSpeaking();
    if (voice.recording === "talk") {
      await finishTalk();
    } else if (!voice.recording && !streaming && !voice.transcribing) {
      await voice.start("talk", {
        onSilence: finishTalk,
        onNoSpeech: async () => {
          await voice.stop({ discard: true });
          toast.message("I didn't hear anything — tap Talk to try again.");
        },
      });
    }
  };

  const talkState = voice.recording === "talk" ? "listening"
    : voice.transcribing && !voice.recording ? "transcribing"
    : voice.speaking ? "speaking"
    : thinking ? "thinking" : "idle";
  const TALK_LABEL = { idle: "Talk", listening: "Listening…", transcribing: "Transcribing…", thinking: "Thinking…", speaking: "Speaking" };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-widget-open"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2.5 rounded-full bg-[#0c1418] hover:bg-[#0f1b21] text-cyan-50 pl-2 pr-5 h-14 shadow-2xl border border-cyan-400/40 transition-all"
          style={{ boxShadow: "0 0 24px rgba(34,211,238,0.25)" }}
        >
          <img src={COMPANY.luchiiMark} alt="Luchii" className="w-10 h-10 object-cover rounded-full" />
          <span className="font-medium tracking-wide">Ask Luchii</span>
        </button>
      )}

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-6 right-6 z-50 w-[92vw] sm:w-[420px] h-[600px] max-h-[82vh] rounded-2xl border border-[#16323a] shadow-2xl flex flex-col overflow-hidden animate-fade-up"
          style={{ background: "linear-gradient(180deg,#0d2027 0%,#0a1216 22%,#080d10 100%)" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#14262d]">
            <div className="flex items-center gap-2.5">
              <img src={COMPANY.luchiiMark} alt="Luchii" className="w-9 h-9 object-cover rounded-full" />
              <span className="text-white text-lg font-semibold tracking-wide">Luchii</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <select
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  data-testid="chat-model-select"
                  className="appearance-none bg-[#0c171c] border border-[#1e3a44] text-cyan-100 text-xs rounded-lg pl-3 pr-7 h-8 outline-none cursor-pointer font-mono"
                >
                  {MODELS.map((m) => <option key={m} value={m}>{m} · {TIER[m]}</option>)}
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-cyan-300 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
              <button onClick={() => setOpen(false)} data-testid="chat-widget-close" className="text-slate-500 hover:text-cyan-300 ml-1">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
            <div className="text-[13px] tracking-[0.45em] text-slate-500 mb-3 select-none">L U C H I I</div>
            <div className="space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${m.role}`}
                    className={`max-w-[88%] text-[15px] leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-cyan-500/15 border border-cyan-400/25 text-cyan-50 rounded-2xl rounded-br-sm px-3.5 py-2.5"
                        : "text-slate-100"
                    }`}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? <span className="text-cyan-400">…</span> : "")}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="px-3 pb-2 pt-1">
            <div className="rounded-2xl border border-[#1c3540] bg-[#0b141a] px-3 pt-2.5 pb-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Message Luchii…"
                data-testid="chat-input"
                className="w-full bg-transparent text-[15px] text-slate-100 placeholder:text-slate-500 outline-none px-1"
              />
              <div className="flex items-center justify-between mt-2.5">
                <div className="flex items-center gap-2">
                  <button className="w-8 h-8 rounded-full bg-[#12222a] text-slate-300 flex items-center justify-center hover:bg-[#162c34]" data-testid="chat-plus">
                    <Plus className="w-4 h-4" />
                  </button>
                  <button
                    onClick={onMic}
                    disabled={voice.recording === "talk" || voice.transcribing}
                    aria-label={voice.recording === "dictate" ? "Stop dictation" : "Dictate message"}
                    data-testid="chat-mic"
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-40 ${
                      voice.recording === "dictate" ? "bg-red-500/20 text-red-400 animate-pulse" : "text-slate-400 hover:text-cyan-300"
                    }`}
                  >
                    {voice.recording === "dictate" ? <Square className="w-3.5 h-3.5" /> : <Mic className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={onTalk}
                    disabled={voice.recording === "dictate" || talkState === "thinking" || (streaming && talkState === "idle")}
                    data-testid="chat-talk"
                    data-state={talkState}
                    className={`flex items-center gap-1.5 h-8 px-3 rounded-full border text-sm transition-colors disabled:opacity-40 ${
                      talkState === "listening" ? "border-red-400/50 text-red-300 bg-red-500/10 animate-pulse"
                        : talkState === "speaking" ? "border-cyan-300/60 text-cyan-200 bg-cyan-400/15"
                        : "border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/10"
                    }`}
                  >
                    {talkState === "listening" ? <Square className="w-3.5 h-3.5" />
                      : talkState === "transcribing" || talkState === "thinking" ? <Loader2 className="w-4 h-4 animate-spin" />
                      : talkState === "speaking" ? <Volume2 className="w-4 h-4" />
                      : <AudioLines className="w-4 h-4" />}
                    <span data-testid="chat-talk-label">{TALK_LABEL[talkState]}</span>
                  </button>
                </div>
                <button
                  onClick={() => send()}
                  disabled={streaming || !input.trim()}
                  data-testid="chat-send"
                  className="w-10 h-10 rounded-full bg-white text-[#0a1216] flex items-center justify-center disabled:opacity-40 hover:bg-cyan-50 transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="text-center text-[11px] text-slate-500 mt-2 px-2">
              Luchii is AI. By using it, you agree to our <span className="underline text-slate-400 cursor-pointer">Terms &amp; Privacy Policy</span>.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
